import { useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { clearPublicLocationsCache } from "../../hooks/usePublicLocations";
import { useSafeFetch } from "../../lib/useSafeFetch";
import { useConfirm } from "../../components/ConfirmDialog";
import { Pencil, Trash2, Plus, X, MapPin, CopyPlus } from "lucide-react";
import { saveSuccess, saveError } from "../../lib/saveBanner";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const DEFAULT_HOURS = DAYS.reduce((acc, d) => ({ ...acc, [d]: { open: "08:00", close: "20:00" } }), {});

const EMPTY_FORM = {
  name: "",
  active: true,
  location_type: "downtown",
  pickup_fee: 0,
  return_fee: 0,
  out_of_hours_fee: 0,
  blackout_enabled: false,
  blackout_from: "22:00",
  blackout_to:   "06:00",
  working_hours: { ...DEFAULT_HOURS },
};

export default function AdminLocations() {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState("");
  const [bulkSource, setBulkSource] = useState(null);

  const { data: items, loading, refetch: load } = useSafeFetch(
    "/admin/locations", { defaultValue: [], asList: true },
  );
  const { confirm, dialog: confirmDialog } = useConfirm();

  const openCreate = () => { setEditing("new"); setForm({ ...EMPTY_FORM, working_hours: { ...DEFAULT_HOURS } }); setError(""); };
  const openEdit = (l) => {
    setEditing(l.id);
    setForm({
      ...l,
      working_hours: { ...DEFAULT_HOURS, ...(l.working_hours || {}) },
    });
    setError("");
  };
  const close = () => { setEditing(null); setError(""); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const payload = {
      ...form,
      location_type: form.location_type || "downtown",
      pickup_fee: Number(form.pickup_fee) || 0,
      return_fee: Number(form.return_fee) || 0,
      out_of_hours_fee: Number(form.out_of_hours_fee) || 0,
      blackout_enabled: form.blackout_enabled === true,
      blackout_from:    form.blackout_from || "22:00",
      blackout_to:      form.blackout_to   || "06:00",
      active: form.active !== false,
    };
    delete payload.id;
    delete payload._id;
    delete payload.created_at;
    try {
      if (editing === "new") await api.post("/admin/locations", payload);
      else await api.put(`/admin/locations/${editing}`, payload);
      clearPublicLocationsCache();
      await load();
      close();
      saveSuccess();
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      setError(msg);
      saveError(msg);
    }
  };

  const handleDelete = async (loc) => {
    const ok = await confirm({
      title:        "Delete location",
      message:      "This will remove the location from every vehicle and future booking.",
      subject:      loc.name,
      subtitle:     "Existing bookings keep the name as a text snapshot; only fresh bookings are affected.",
      confirmLabel: "Delete location",
      testId:       "confirm-delete-location",
    });
    if (!ok) return;
    await api.delete(`/admin/locations/${loc.id}`);
    clearPublicLocationsCache();
    load();
  };

  const toggleActive = async (l) => {
    const payload = {
      ...l,
      active: !l.active,
      working_hours: { ...DEFAULT_HOURS, ...(l.working_hours || {}) },
    };
    delete payload.id;
    delete payload._id;
    delete payload.created_at;
    await api.put(`/admin/locations/${l.id}`, payload);
    clearPublicLocationsCache();
    load();
  };

  return (
    <div data-testid="admin-locations-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 dark:text-neutral-400">Network</p>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight mt-2">
            Pickup & return <em className="italic">locations</em>.
          </h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-2 max-w-xl">
            Where you deliver and collect vehicles. Each location supports its own fee structure and operating hours; charges are added to the booking automatically.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="bg-black text-white dark:bg-white dark:text-black px-5 py-2.5 text-sm hover:opacity-90 transition-opacity inline-flex items-center gap-2 rounded"
          data-testid="add-location"
        >
          <Plus size={14} /> Add Location
        </button>
      </div>

      <div className="bg-white dark:bg-[#1F2937] border border-black/10 dark:border-white/10 rounded overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-left text-[10px] uppercase tracking-[0.18em] text-neutral-500 dark:text-neutral-400 border-b border-black/8 dark:border-white/10 bg-[#FAF9F6] dark:bg-[#111827]">
            <tr>
              <th className="py-3 px-4">Location</th>
              <th className="py-3 px-4">Pickup</th>
              <th className="py-3 px-4">Return</th>
              <th className="py-3 px-4">Out-of-Hours</th>
              <th className="py-3 px-4">Blackout</th>
              <th className="py-3 px-4">Status</th>
              <th className="py-3 px-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="py-8 text-center text-neutral-500">Loading…</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={7} className="py-10 text-center text-neutral-500">No locations yet — add one to get started.</td></tr>
            )}
            {!loading && items.map((l) => (
              <tr key={l.id} className="border-b border-black/5 dark:border-white/5" data-testid={`loc-row-${l.id}`}>
                <td className="py-3 px-4">
                  <div className="flex items-center gap-2">
                    <MapPin size={14} className="text-neutral-400" />
                    <span className="font-medium">{l.name}</span>
                  </div>
                </td>
                <td className="py-3 px-4 tabular-nums">€{(l.pickup_fee || 0).toFixed(2)}</td>
                <td className="py-3 px-4 tabular-nums">€{(l.return_fee || 0).toFixed(2)}</td>
                <td className="py-3 px-4 tabular-nums">€{(l.out_of_hours_fee || 0).toFixed(2)}</td>
                <td className="py-3 px-4">
                  {l.blackout_enabled ? (
                    <span className="text-[10px] px-2 py-0.5 rounded bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300 tabular-nums" data-testid={`blackout-window-${l.id}`}>
                      {l.blackout_from} → {l.blackout_to}
                    </span>
                  ) : (
                    <span className="text-[10px] text-neutral-400">—</span>
                  )}
                </td>
                <td className="py-3 px-4">
                  <button
                    onClick={() => toggleActive(l)}
                    className={`text-xs px-2 py-0.5 rounded ${l.active ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300" : "bg-neutral-200 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300"}`}
                    data-testid={`toggle-${l.id}`}
                  >
                    {l.active ? "Active" : "Inactive"}
                  </button>
                </td>
                <td className="py-3 px-4 text-right">
                  {l.blackout_enabled && items.length > 1 && (
                    <button
                      onClick={() => setBulkSource(l)}
                      className="p-1.5 hover:bg-black/5 dark:hover:bg-white/10 rounded text-neutral-500 hover:text-black dark:hover:text-white mr-1"
                      title="Copy blackout hours to other locations"
                      data-testid={`copy-blackout-${l.id}`}
                    >
                      <CopyPlus size={14} />
                    </button>
                  )}
                  <button onClick={() => openEdit(l)} className="p-1.5 hover:bg-black/5 dark:hover:bg-white/10 rounded" data-testid={`edit-${l.id}`}><Pencil size={14} /></button>
                  <button onClick={() => handleDelete(l)} className="p-1.5 hover:bg-red-50 dark:hover:bg-rose-900/30 text-red-600 dark:text-rose-400 rounded ml-1" data-testid={`delete-${l.id}`}><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={close}>
          <div
            className="bg-white dark:bg-[#1F2937] text-[#0A0A0A] dark:text-[#F9FAFB] max-w-3xl w-full p-8 max-h-[90vh] overflow-y-auto rounded border border-black/10 dark:border-white/10"
            onClick={(e) => e.stopPropagation()}
            data-testid="location-form-modal"
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-display text-3xl">{editing === "new" ? "Add Location" : "Edit Location"}</h2>
              <button onClick={close} aria-label="Close"><X /></button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
                <Field label="Name">
                  <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="loc-name" />
                </Field>
                <Field label="Active?">
                  <div className="pt-2">
                    <Toggle checked={form.active !== false} onChange={(v) => setForm({ ...form, active: v })} testId="loc-active-toggle" />
                  </div>
                </Field>
                <Field label="Location type">
                  <select
                    value={form.location_type || "downtown"}
                    onChange={(e) => setForm({ ...form, location_type: e.target.value })}
                    className="w-full bg-transparent border-b border-black/15 dark:border-white/15 py-2 outline-none focus:border-black dark:focus:border-white text-sm"
                    data-testid="loc-type"
                  >
                    <option value="downtown">Downtown / office</option>
                    <option value="airport">Airport</option>
                    <option value="port">Port / marina</option>
                    <option value="hotel">Hotel / villa / apartment</option>
                  </select>
                </Field>
                <Field label="Pickup fee (€)">
                  <Input type="number" step="0.01" value={form.pickup_fee} onChange={(e) => setForm({ ...form, pickup_fee: e.target.value })} data-testid="loc-pickup-fee" />
                </Field>
                <Field label="Return fee (€)">
                  <Input type="number" step="0.01" value={form.return_fee} onChange={(e) => setForm({ ...form, return_fee: e.target.value })} data-testid="loc-return-fee" />
                </Field>
                <Field label="Out-of-hours fee (€)">
                  <Input type="number" step="0.01" value={form.out_of_hours_fee} onChange={(e) => setForm({ ...form, out_of_hours_fee: e.target.value })} data-testid="loc-oh-fee" />
                </Field>
              </div>

              {/* v3.8.6 — Blackout / No-Service window */}
              <div className="border border-black/10 dark:border-white/10 rounded p-4 bg-neutral-50/60 dark:bg-white/5" data-testid="loc-blackout-panel">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.blackout_enabled === true}
                    onChange={(e) => setForm({ ...form, blackout_enabled: e.target.checked })}
                    className="mt-0.5 accent-black dark:accent-white"
                    data-testid="loc-blackout-enabled"
                  />
                  <div>
                    <p className="text-sm font-medium">Enable Blackout Hours (No Service)</p>
                    <p className="text-[11px] text-neutral-500 dark:text-neutral-400 mt-0.5 leading-snug">
                      No pickups or returns allowed during this window. Other out-of-operating hours will apply the out-of-hours fee.
                    </p>
                  </div>
                </label>
                {form.blackout_enabled && (
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <Field label="Blackout from">
                      <Input
                        type="time"
                        value={form.blackout_from || "22:00"}
                        onChange={(e) => setForm({ ...form, blackout_from: e.target.value })}
                        data-testid="loc-blackout-from"
                      />
                    </Field>
                    <Field label="Blackout to">
                      <Input
                        type="time"
                        value={form.blackout_to || "06:00"}
                        onChange={(e) => setForm({ ...form, blackout_to: e.target.value })}
                        data-testid="loc-blackout-to"
                      />
                    </Field>
                  </div>
                )}
              </div>

              <div>
                <p className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 dark:text-neutral-400 mb-3">Operating hours (open → close)</p>
                <div className="border border-black/10 dark:border-white/10 rounded divide-y divide-black/5 dark:divide-white/5">
                  {DAYS.map((d) => (
                    <div key={d} className="grid grid-cols-[120px_1fr_auto_1fr] gap-3 items-center py-2.5 px-3" data-testid={`hours-row-${d}`}>
                      <span className="text-sm">{d}</span>
                      <input
                        type="time"
                        value={form.working_hours?.[d]?.open || "08:00"}
                        onChange={(e) => setForm({
                          ...form,
                          working_hours: { ...form.working_hours, [d]: { ...(form.working_hours[d] || {}), open: e.target.value } },
                        })}
                        className="bg-transparent border-b border-black/15 dark:border-white/15 py-1 outline-none focus:border-black dark:focus:border-white text-sm"
                        data-testid={`open-${d}`}
                      />
                      <span className="text-neutral-400">→</span>
                      <input
                        type="time"
                        value={form.working_hours?.[d]?.close || "20:00"}
                        onChange={(e) => setForm({
                          ...form,
                          working_hours: { ...form.working_hours, [d]: { ...(form.working_hours[d] || {}), close: e.target.value } },
                        })}
                        className="bg-transparent border-b border-black/15 dark:border-white/15 py-1 outline-none focus:border-black dark:focus:border-white text-sm"
                        data-testid={`close-${d}`}
                      />
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-neutral-500 dark:text-neutral-400 mt-2">
                  Pickups/returns outside these windows automatically apply the out-of-hours fee.
                </p>
              </div>

              {error && <p className="text-red-600 dark:text-rose-400 text-sm">{error}</p>}

              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={close} className="border border-black/15 dark:border-white/15 px-6 py-2.5 text-sm hover:border-black dark:hover:border-white transition-colors rounded">Cancel</button>
                <button type="submit" className="bg-black text-white dark:bg-white dark:text-black px-6 py-2.5 text-sm hover:opacity-90 transition-opacity rounded" data-testid="loc-submit">
                  {editing === "new" ? "Add location" : "Save changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {bulkSource && (
        <BulkBlackoutModal
          source={bulkSource}
          targets={items.filter((l) => l.id !== bulkSource.id)}
          onClose={() => setBulkSource(null)}
          onApplied={async () => {
            await load();
            clearPublicLocationsCache();
            setBulkSource(null);
            saveSuccess("Blackout hours copied");
          }}
        />
      )}
      {confirmDialog}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 dark:text-neutral-400 block mb-2">{label}</span>
      {children}
    </label>
  );
}

function Input(props) {
  return (
    <input
      {...props}
      className="w-full bg-transparent border-b border-black/15 dark:border-white/15 py-2 outline-none focus:border-black dark:focus:border-white transition-colors text-sm"
    />
  );
}

function Toggle({ checked, onChange, testId }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative w-11 h-6 rounded-full transition-colors ${checked ? "bg-emerald-600 dark:bg-emerald-500" : "bg-neutral-300 dark:bg-neutral-700"}`}
      data-testid={testId}
      aria-pressed={checked}
    >
      <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${checked ? "translate-x-5" : "translate-x-0"}`} />
    </button>
  );
}

/**
 * BulkBlackoutModal (v3.8.7) — copy one location's blackout window to
 * any subset of the other locations in one click. Idempotent: applying
 * the same source twice is a no-op.
 */
function BulkBlackoutModal({ source, targets, onClose, onApplied }) {
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAll = () => setSelected(new Set(targets.map((t) => t.id)));
  const clearAll  = () => setSelected(new Set());

  const submit = async () => {
    setErr("");
    if (selected.size === 0) { setErr("Pick at least one target location."); return; }
    setBusy(true);
    try {
      await api.post("/admin/locations/bulk-blackout", {
        source_id:  source.id,
        target_ids: Array.from(selected),
      });
      await onApplied();
    } catch (e) {
      const msg = formatApiError(e?.response?.data?.detail) || e.message;
      setErr(msg);
      saveError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[55] flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
      data-testid="bulk-blackout-modal"
    >
      <div
        className="bg-white dark:bg-[#1F2937] max-w-lg w-full p-6 rounded border border-black/10 dark:border-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start mb-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">Bulk copy</p>
            <h3 className="font-display text-2xl mt-1">Blackout hours</h3>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
              Copy <strong>{source.blackout_from} → {source.blackout_to}</strong> from
              <em> {source.name}</em> to the locations you tick below.
            </p>
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="bulk-close"><X size={18} /></button>
        </div>

        <div className="flex items-center justify-between text-[11px] mb-2">
          <span className="text-neutral-500 dark:text-neutral-400">{selected.size} / {targets.length} selected</span>
          <div className="flex gap-3">
            <button type="button" onClick={selectAll} className="underline text-neutral-600 dark:text-neutral-300 hover:text-black dark:hover:text-white" data-testid="bulk-select-all">All</button>
            <button type="button" onClick={clearAll} className="underline text-neutral-600 dark:text-neutral-300 hover:text-black dark:hover:text-white" data-testid="bulk-clear-all">None</button>
          </div>
        </div>

        <ul className="border border-black/10 dark:border-white/10 rounded max-h-72 overflow-y-auto divide-y divide-black/5 dark:divide-white/5" data-testid="bulk-target-list">
          {targets.map((t) => {
            const on = selected.has(t.id);
            return (
              <li key={t.id}>
                <label className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-black/5 dark:hover:bg-white/5">
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={() => toggle(t.id)}
                    className="accent-black dark:accent-white"
                    data-testid={`bulk-target-${t.id}`}
                  />
                  <div className="flex-1">
                    <p className="text-sm">{t.name}</p>
                    <p className="text-[10px] text-neutral-500 dark:text-neutral-400">
                      {t.blackout_enabled
                        ? `Currently ${t.blackout_from} → ${t.blackout_to}`
                        : "No blackout set — will inherit from source"}
                    </p>
                  </div>
                </label>
              </li>
            );
          })}
        </ul>

        {err && <p className="text-xs text-rose-600 dark:text-rose-400 mt-3">{err}</p>}

        <div className="flex justify-end gap-2 mt-5">
          <button type="button" onClick={onClose} className="border border-black/15 dark:border-white/15 px-5 py-2 text-sm rounded hover:border-black dark:hover:border-white" data-testid="bulk-cancel">
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy || selected.size === 0}
            className="bg-black text-white dark:bg-white dark:text-black px-5 py-2 text-sm rounded hover:opacity-90 disabled:opacity-40"
            data-testid="bulk-apply"
          >
            {busy ? "Copying…" : `Copy to ${selected.size || 0}`}
          </button>
        </div>
      </div>
    </div>
  );
}

